import { useNavigate } from 'react-router';
import {
  OutlineDocumentTextIcon,
  OutlineDollarCircleIcon,
  OutlineUsersIcon,
} from '@tempmee/react-icons';
import type { SettingsMenuRow } from '@tempmee/react-blocks';
import { useHasInvoices } from '~/domain/Invoices';
import { useAppSelector } from '~/hooks/useAppSelector';
import { routes } from '~/routes';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';
import { toViewedOfficePath } from '~/utils/viewedOfficeScope';

const ORGANIZATION: TViewedOfficeScope = { kind: 'organization' };

/**
 * The Organization section above the locations list (GOTU-19472, a half step
 * toward the Organization phase): Payment History and Invoices rolled up across
 * every location (`?scope=organization`), Invoices only when there are any, and
 * Worker Type Management when `/auth/me` answers `canManageWorkerTypes` (the
 * server's rule: more than one location, one of them in a dual-model state).
 * Worker Type Management reads the signed-in account, so it opens without a scope.
 */
export const useOrganizationRows = (): SettingsMenuRow[] => {
  const navigate = useNavigate();
  const canManageWorkerTypes = useAppSelector((state) => state.user.canManageWorkerTypes);
  const hasInvoices = useHasInvoices(ORGANIZATION);

  const rows: Array<SettingsMenuRow | false> = [
    {
      id: 'payment-history',
      label: 'Payment History',
      icon: <OutlineDollarCircleIcon />,
      onSelect: () => navigate(toViewedOfficePath(routes.paymentHistory, ORGANIZATION)),
      testID: 'payment-history',
    },
    hasInvoices && {
      id: 'invoices',
      label: 'Invoices',
      icon: <OutlineDocumentTextIcon />,
      onSelect: () => navigate(toViewedOfficePath(routes.invoices, ORGANIZATION)),
      testID: 'invoices',
    },
    canManageWorkerTypes && {
      id: 'worker-type-management',
      label: 'Worker Type Management',
      icon: <OutlineUsersIcon />,
      onSelect: () => navigate(routes.workerClassificationManagement),
      testID: 'worker-type-management',
    },
  ];

  return rows.filter((row): row is SettingsMenuRow => !!row);
};
